import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { env } from "../../../env";
import axiosInstance from "../../globalInspector/axiosInstance";
import CommonToast from "../../../components/_utils/CommonToast";
import ICONS from "../../../assets/Icons";

const baseUrl = env.REACT_APP_BASE_URL;

export const getAllDevices = createAsyncThunk(
  "getAllDevices",
  async (values, { rejectWithValue }) => {
    const { page_no, page_size } = values;
    try {
      const response = await axiosInstance.get(`${baseUrl}/devices`, {
        headers: {
          "Content-Type": "application/json",
          "X-Api-Key": sessionStorage.getItem("q2p_token"),
        },
        params: {
          page_no: page_no,
          page_size: page_size,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.errors?.token_expired?.[0] ||
        error?.response?.data?.errors?.user_unauthorized?.[0] ||
        error?.response?.data?.errors?.base?.[0] ||
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        (typeof error?.message === "string" ? error.message : null) ||
        "Failed to fetch devices.";

      // Never pass Error/object into CommonToast — React will crash (Oops page)
      if (typeof message === "string") {
        CommonToast({
          text: message,
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
          buttonNeeded: false,
        });
      }

      console.error("Error", error);
      return rejectWithValue({
        status: error?.response?.status || null,
        message:
          typeof message === "string" ? message : "Failed to fetch devices.",
      });
    }
  },
);

export const getAPIToken = createAsyncThunk(
  "getAPIToken",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `${baseUrl}/devices/${id}/generate_api_token`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
        },
      );

      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error.message ||
        "Something went wrong while generating API token.";

      // Show toast
      CommonToast({
        text: message,
        icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        variant: "var(--error)",
        buttonNeeded: false,
      });
      return rejectWithValue({
        status: error?.response?.status || null,
        message,
      });
    }
  },
);

export const deleteDevice = createAsyncThunk(
  "deleteDevice",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(`${baseUrl}/devices/${id}`, {
        headers: {
          "Content-Type": "application/json",
          "X-Api-Key": sessionStorage.getItem("q2p_token"),
        },
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updateFcmToken = createAsyncThunk(
  "updateFcmToken",
  async (values, { rejectWithValue }) => {
    const { deviceId, token } = values;
    try {
      const response = await axiosInstance.put(
        `${baseUrl}/devices/${deviceId}/fcm_token`,
        {
          fcm_token: token,
        },
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
        },
      );

      return response.data;
    } catch (error) {
      if (error?.response?.status !== 401) {
        const message =
          error?.response?.data?.message ||
          error.message ||
          "Something went wrong while updating FCM token.";

        // Show toast
        CommonToast({
          text: message,
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
          buttonNeeded: false,
        });
        return rejectWithValue({
          status: error?.response?.status || null,
          message,
        });
      }
    }
  },
);

const devicesSlice = createSlice({
  name: "devices",
  initialState: {
    allDevicesData: null,
    allDevicesDataLoading: null,
    allDevicesDataError: null,

    deleteDeviceStatus: null,
    deleteDeviceLoading: null,
    deleteDeviceError: null,

    deviceData: null,

    apiToken: null,
    apiTokenLoading: null,
    apiTokenError: null,

    updateFcmTokenLoading: null,
    updateFcmTokenError: null,
  },
  reducers: {
    setDeviceData: (state, action) => {
      state.deviceData = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(getAllDevices.pending, (state, action) => {
      state.allDevicesDataLoading = true;
      state.allDevicesDataError = null;
    });
    builder.addCase(getAllDevices.fulfilled, (state, action) => {
      state.allDevicesDataLoading = false;
      state.allDevicesData = action.payload;
    });
    builder.addCase(getAllDevices.rejected, (state, action) => {
      state.allDevicesDataLoading = false;
      state.allDevicesDataError = true;
      state.allDevicesData = null;
    });

    builder.addCase(deleteDevice.pending, (state, action) => {
      state.deleteDeviceLoading = true;
    });
    builder.addCase(deleteDevice.fulfilled, (state, action) => {
      state.deleteDeviceLoading = false;
      state.deleteDeviceStatus = action.payload;
    });
    builder.addCase(deleteDevice.rejected, (state, action) => {
      state.deleteDeviceError = true;
      state.deleteDeviceStatus = null;
    });

    builder.addCase(getAPIToken.pending, (state, action) => {
      state.apiTokenLoading = true;
    });
    builder.addCase(getAPIToken.fulfilled, (state, action) => {
      state.apiTokenLoading = false;
      state.apiToken = action.payload;
    });
    builder.addCase(getAPIToken.rejected, (state, action) => {
      state.apiTokenError = true;
      state.apiToken = null;
    });

    builder.addCase(updateFcmToken.pending, (state, action) => {
      state.updateFcmTokenLoading = true;
    });
    builder.addCase(updateFcmToken.fulfilled, (state, action) => {
      state.updateFcmTokenLoading = false;
      state.updateFcmTokenStatus = action.payload;
    });
    builder.addCase(updateFcmToken.rejected, (state, action) => {
      state.updateFcmTokenError = true;
    });
  },
});

export const { setDeviceData } = devicesSlice.actions;
export default devicesSlice.reducer;
